import { useEffect, useState } from "react";
import {
  Users,
  TrendUp,
  ChartBar,
  Sparkle,
  Crown,
  CurrencyInr,
  ChatCircleDots,
  ShieldStar,
  CircleNotch,
  MagnifyingGlass,
  Cube,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const usd = (n) => `$${Number(n || 0).toFixed(4)}`;
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const PLAN_COLORS = {
  free: "bg-[#F5EFE6] text-[#4A5568]",
  plus: "bg-[#3B5BDB]/10 text-[#3B5BDB]",
  pro: "bg-[#D4AF37]/15 text-[#8a6d1c]",
};

export default function AdminUsers() {
  const [overview, setOverview] = useState(null);
  const [aiUsage, setAiUsage] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [tier, setTier] = useState(null);
  const [users, setUsers] = useState({ total: 0, users: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [ovr, ai, rev, tier] = await Promise.all([
        api.get("/admin/analytics/overview"),
        api.get("/admin/analytics/ai-usage?days=30"),
        api.get("/admin/analytics/revenue").catch(() => ({ data: null })),
        api.get("/admin/analytics/cost-per-tier?days=30").catch(() => ({ data: null })),
      ]);
      setOverview(ovr.data);
      setAiUsage(ai.data);
      setRevenue(rev.data);
      setTier(tier.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load analytics");
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (planFilter) params.set("plan", planFilter);
    params.set("limit", "50");
    try {
      const r = await api.get(`/admin/users?${params.toString()}`);
      setUsers(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load users");
    }
  };

  useEffect(() => {
    load();
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced reload on filter change
  useEffect(() => {
    const t = setTimeout(loadUsers, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, planFilter]);

  const updatePlan = async (user, plan) => {
    try {
      await api.patch(`/admin/users/${user.id}`, { plan });
      toast.success(`${user.email} → ${plan}`);
      loadUsers();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold flex items-center gap-2">
            <Sparkle size={14} weight="fill" className="text-[#D4AF37]" /> Analytics
          </p>
          <h1 className="font-display text-3xl sm:text-4xl mt-2 text-[#0A1128]">
            Users &amp; analytics
          </h1>
          <p className="mt-2 text-sm text-[#4A5568] max-w-2xl">
            Live counts from MongoDB. MAU/DAU is tracked via{" "}
            <code className="px-1.5 py-0.5 bg-[#F5EFE6] rounded text-xs">last_active_at</code>{" "}
            (refreshed once per minute on any authenticated request).
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-8 mc-card p-10 flex items-center justify-center gap-3 text-[#4A5568]">
          <CircleNotch className="animate-spin" weight="bold" /> Loading analytics...
        </div>
      ) : (
        <>
          {/* Top-level KPIs */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="kpi-row">
            <Kpi icon={Users} label="Total users" value={fmt(overview.users.total)} accent />
            <Kpi icon={TrendUp} label="MAU (30d)" value={fmt(overview.users.mau_30d)} />
            <Kpi icon={ChartBar} label="DAU" value={fmt(overview.users.dau_today)} />
            <Kpi icon={Sparkle} label="New today" value={fmt(overview.users.new_today)} />
          </div>

          {/* Plan breakdown */}
          <div className="mt-5 grid lg:grid-cols-2 gap-5">
            <div className="mc-card p-6" data-testid="plan-breakdown">
              <div className="flex items-center gap-2">
                <Crown size={20} weight="duotone" className="text-[#D4AF37]" />
                <h2 className="font-display text-xl text-[#0A1128]">Users by plan</h2>
              </div>
              <ul className="mt-4 space-y-2.5">
                {Object.entries(overview.users.by_plan || {}).map(([plan, count]) => {
                  const total = overview.users.total || 1;
                  const pct = Math.round((count / total) * 100);
                  return (
                    <li key={plan}>
                      <div className="flex justify-between text-sm">
                        <span className={`px-2 py-0.5 rounded text-xs uppercase font-medium ${PLAN_COLORS[plan] || "bg-[#F5EFE6] text-[#4A5568]"}`}>
                          {plan}
                        </span>
                        <span className="tabular-nums text-[#0A1128] font-medium">
                          {fmt(count)} <span className="text-[#4A5568] text-xs">({pct}%)</span>
                        </span>
                      </div>
                      <div className="mt-1.5 h-1.5 bg-[#F5EFE6] rounded-full overflow-hidden">
                        <div
                          className={`h-full ${plan === "pro" ? "bg-[#D4AF37]" : plan === "plus" ? "bg-[#3B5BDB]" : "bg-[#0A1128]/30"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-4 text-xs text-[#4A5568]/80 flex items-center gap-1.5">
                <ShieldStar size={12} weight="duotone" /> {fmt(overview.users.admins)} admin{overview.users.admins === 1 ? "" : "s"} ·{" "}
                {fmt(overview.users.new_last_7_days)} new in 7d ·{" "}
                {fmt(overview.users.new_last_30_days)} new in 30d
              </p>
            </div>

            {/* Engagement */}
            <div className="mc-card p-6" data-testid="engagement-card">
              <div className="flex items-center gap-2">
                <ChatCircleDots size={20} weight="duotone" className="text-[#3B5BDB]" />
                <h2 className="font-display text-xl text-[#0A1128]">Engagement</h2>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <Mini label="Conversations" value={fmt(overview.engagement.conversations_total)} />
                <Mini label="Messages" value={fmt(overview.engagement.messages_total)} />
                <Mini label="Waitlist" value={fmt(overview.engagement.waitlist_total)} />
                <Mini label="Active alerts" value={fmt(overview.engagement.active_alerts)} />
                <Mini label="Documents" value={`${fmt(overview.engagement.documents_total)} (${fmt(overview.engagement.public_documents)} public)`} />
                <Mini label="Paid (30d)" value={fmt(overview.engagement.paid_payments_30d)} />
              </div>
            </div>
          </div>

          {/* Revenue + Cost-per-tier (v17) */}
          {(revenue || tier) && (
            <div className="mt-5 grid lg:grid-cols-2 gap-5">
              {revenue && (
                <div className="mc-card p-6" data-testid="revenue-card">
                  <div className="flex items-center gap-2">
                    <CurrencyInr size={20} weight="duotone" className="text-[#3B5BDB]" />
                    <h2 className="font-display text-xl text-[#0A1128]">Revenue (MRR)</h2>
                  </div>
                  <p className="mt-3 font-display text-4xl text-[#3B5BDB] tabular-nums">
                    {inr(revenue.current_mrr_inr)}
                    <span className="text-xs text-[#4A5568] ml-2 font-normal">/ month</span>
                  </p>
                  <p className="mt-1 text-xs text-[#4A5568]">
                    {revenue.active_paid_users.plus} Plus · {revenue.active_paid_users.pro} Pro · MoM growth{" "}
                    <span className={revenue.growth_mom_pct >= 0 ? "text-[#16A34A]" : "text-[#DC2626]"}>
                      {revenue.growth_mom_pct >= 0 ? "+" : ""}{revenue.growth_mom_pct}%
                    </span>
                  </p>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                    {revenue.trailing_3_months.map((m) => (
                      <div key={m.month} className="text-center">
                        <p className="text-[10px] uppercase tracking-wider text-[#4A5568]">{m.month}</p>
                        <p className="mt-1 font-medium text-[#0A1128] tabular-nums">{inr(m.revenue_inr)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#E8DFD1]">
                    <p className="text-[10px] uppercase tracking-wider text-[#4A5568]">3-mo projection</p>
                    <div className="mt-1 grid grid-cols-3 gap-2 text-xs">
                      {revenue.projection_3_months_inr.map((p) => (
                        <div key={p.month_offset} className="text-center">
                          <p className="text-[10px] text-[#4A5568]">M+{p.month_offset}</p>
                          <p className="font-medium text-[#3B5BDB] tabular-nums">{inr(p.projected_inr)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {tier && (
                <div className="mc-card p-6" data-testid="cost-per-tier-card">
                  <div className="flex items-center gap-2">
                    <Cube size={20} weight="duotone" className="text-[#3B5BDB]" />
                    <h2 className="font-display text-xl text-[#0A1128]">AI cost per plan tier (30d)</h2>
                  </div>
                  <p className="mt-1 text-xs text-[#4A5568]">
                    Surface which plans subsidise which. Free with high cost = upsell candidates.
                  </p>
                  <table className="mt-4 w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-[#4A5568] border-b border-[#E8DFD1]">
                        <th className="text-left py-2">Plan</th>
                        <th className="text-right py-2">Users</th>
                        <th className="text-right py-2">Calls</th>
                        <th className="text-right py-2">Cost</th>
                        <th className="text-right py-2">Per user</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8DFD1]">
                      {Object.entries(tier.by_plan).map(([p, row]) => (
                        <tr key={p} data-testid={`tier-row-${p}`}>
                          <td className="py-2">
                            <span className={`px-2 py-0.5 rounded text-xs uppercase font-medium ${PLAN_COLORS[p] || "bg-[#F5EFE6] text-[#4A5568]"}`}>{p}</span>
                          </td>
                          <td className="py-2 text-right tabular-nums">{fmt(row.users)}</td>
                          <td className="py-2 text-right tabular-nums">{fmt(row.calls)}</td>
                          <td className="py-2 text-right tabular-nums">{usd(row.cost_usd)}</td>
                          <td className="py-2 text-right tabular-nums font-medium">{usd(row.cost_per_user_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* AI Usage / cost */}
          {aiUsage && (
            <div className="mt-5 mc-card p-6" data-testid="ai-usage-card">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Cube size={20} weight="duotone" className="text-[#3B5BDB]" />
                  <h2 className="font-display text-xl text-[#0A1128]">AI usage &amp; cost (30d)</h2>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-[#4A5568]">Calls: <strong className="text-[#0A1128]">{fmt(aiUsage.total.calls)}</strong></span>
                  <span className="text-[#4A5568]">Tokens: <strong className="text-[#0A1128]">{fmt(aiUsage.total.tokens)}</strong></span>
                  <span className="text-[#4A5568]">Cost: <strong className="text-[#3B5BDB] tabular-nums">{usd(aiUsage.total.cost_usd)}</strong></span>
                </div>
              </div>
              {aiUsage.by_model.length === 0 ? (
                <p className="mt-4 text-sm text-[#4A5568]/80">No AI calls in the last 30 days.</p>
              ) : (
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[11px] uppercase tracking-wider text-[#4A5568] border-b border-[#E8DFD1]">
                        <th className="text-left py-2 pr-3">Model</th>
                        <th className="text-right py-2 px-3 tabular-nums">Calls</th>
                        <th className="text-right py-2 px-3 tabular-nums">Tokens</th>
                        <th className="text-right py-2 px-3 tabular-nums">Avg latency</th>
                        <th className="text-right py-2 px-3 tabular-nums">Errors</th>
                        <th className="text-right py-2 pl-3 tabular-nums">Cost (USD)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E8DFD1]">
                      {aiUsage.by_model.map((m) => (
                        <tr key={m.model} data-testid={`ai-row-${m.model}`}>
                          <td className="py-2 pr-3 text-[#0A1128] font-mono text-xs">{m.model}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{fmt(m.calls)}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{fmt(m.total_tokens)}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{fmt(m.avg_latency_ms)}ms</td>
                          <td className={`py-2 px-3 text-right tabular-nums ${m.errors > 0 ? "text-[#DC2626]" : "text-[#4A5568]"}`}>{m.errors}</td>
                          <td className="py-2 pl-3 text-right tabular-nums font-medium text-[#0A1128]">{usd(m.cost_usd)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Users table */}
          <div className="mt-7" data-testid="users-list">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <h2 className="font-display text-xl text-[#0A1128] flex items-center gap-2">
                <Users size={20} weight="duotone" className="text-[#3B5BDB]" /> Users
              </h2>
              <span className="text-xs text-[#4A5568]">({users.total})</span>
              <div className="flex-1"></div>
              <div className="relative">
                <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-2.5 text-[#4A5568]" />
                <input
                  data-testid="users-search"
                  type="search"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, email, phone..."
                  className="h-9 pl-7 pr-3 rounded-md border border-[#E8DFD1] bg-white text-sm outline-none focus:border-[#3B5BDB]"
                />
              </div>
              <select
                data-testid="users-plan-filter"
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="h-9 px-2 rounded-md border border-[#E8DFD1] bg-white text-sm outline-none focus:border-[#3B5BDB]"
              >
                <option value="">All plans</option>
                <option value="free">Free</option>
                <option value="plus">Plus</option>
                <option value="pro">Pro</option>
              </select>
            </div>
            <div className="mc-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-[#4A5568] border-b border-[#E8DFD1]">
                    <th className="text-left p-3">User</th>
                    <th className="text-left p-3">Plan</th>
                    <th className="text-left p-3">Role</th>
                    <th className="text-left p-3">Joined</th>
                    <th className="text-left p-3">Last active</th>
                    <th className="text-right p-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8DFD1]">
                  {users.users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}>
                      <td className="p-3">
                        <p className="font-medium text-[#0A1128]">{u.name || "—"}</p>
                        <p className="text-xs text-[#4A5568]">{u.email}</p>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded text-[11px] uppercase font-medium ${PLAN_COLORS[u.plan] || "bg-[#F5EFE6] text-[#4A5568]"}`}>
                          {u.plan}
                        </span>
                      </td>
                      <td className="p-3 text-xs">{u.role || "user"}</td>
                      <td className="p-3 text-xs text-[#4A5568] tabular-nums">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="p-3 text-xs text-[#4A5568] tabular-nums">
                        {u.last_active_at ? new Date(u.last_active_at).toLocaleString() : "Never"}
                      </td>
                      <td className="p-3 text-right">
                        <select
                          data-testid={`user-plan-select-${u.id}`}
                          value={u.plan || "free"}
                          onChange={(e) => updatePlan(u, e.target.value)}
                          className="h-7 px-2 rounded border border-[#E8DFD1] bg-white text-xs outline-none focus:border-[#3B5BDB]"
                        >
                          <option value="free">Free</option>
                          <option value="plus">Plus</option>
                          <option value="pro">Pro</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.users.length === 0 && (
                <p className="p-8 text-center text-sm text-[#4A5568]">No users match these filters.</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent = false }) {
  return (
    <div
      className={`mc-card p-4 ${accent ? "border-l-4 border-[#3B5BDB]" : ""}`}
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-2 text-[#4A5568]">
        <Icon size={16} weight="duotone" className="text-[#3B5BDB]" />
        <p className="text-[10px] uppercase tracking-wider">{label}</p>
      </div>
      <p className={`mt-2 font-display ${accent ? "text-[#3B5BDB]" : "text-[#0A1128]"} text-3xl tabular-nums`}>{value}</p>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-[#4A5568]">{label}</p>
      <p className="mt-1 text-xl font-display text-[#0A1128] tabular-nums">{value}</p>
    </div>
  );
}
